/**
 * A REGRA do eSocial: o cronograma de eventos de uma competência e os prazos.
 * Módulo PURO.
 *
 * ---------------------------------------------------------------------------
 * O relógio entra por PARÂMETRO, e o feriado também
 * ---------------------------------------------------------------------------
 * Nenhuma função aqui chama `new Date()` sem argumento — a competência e a data
 * de referência entram de fora, para tudo poder ser provado com qualquer mês. É
 * a disciplina de `anosDaDasn` no simulador do MEI.
 *
 * E o ajuste de vencimento recebe `ehFeriado` como função opcional. A origem
 * recuava só de sábado e domingo, ignorando feriado — o que faz a ferramenta
 * ensinar o dia 20 como vencimento quando ele cai num feriado nacional e o
 * recolhimento antecipa. Não corrigi cravando uma lista de feriados aqui: **há
 * uma frente construindo o Calendário Tributário Federal neste mesmo hub**, com
 * a lista legal, e duas listas de feriados seriam duas definições do mesmo
 * calendário — o defeito de "agregar acesso em JavaScript" com outra roupa.
 *
 * Então a porta fica aberta e nomeada: quando `calendarioFiscal` estiver
 * commitado, passar a função dele aqui é uma linha. Enquanto não estiver, o
 * cronograma recua de fim de semana e a tela diz que feriado não está
 * considerado.
 */

import { calcularHolerite, centavos, type EntradaHolerite, type ParametrosFolha, type ResultadoHolerite } from "./folhaDp";
import { calcularEncargos, type EntradaEncargos, type ResultadoEncargos } from "./encargosDp";

export type CategoriaEvento = "tabela" | "nao-periodico" | "periodico" | "reinf";

export interface DefinicaoEvento {
  codigo: string;
  nome: string;
  categoria: CategoriaEvento;
  prazo: string;
  /**
   * Dia do mês seguinte em que vence, ou `"evento"` quando o prazo é contado do
   * fato e não do calendário (admissão, desligamento).
   */
  diaLimite: number | "evento";
}

/**
 * O catálogo dos eventos, na ordem em que se transmitem.
 *
 * Ordem importa e está no dado, não num `sort` da tela: tabelas antes dos
 * não-periódicos, não-periódicos antes dos periódicos, e o fechamento (S-1299)
 * depois de tudo. Transmitir fora dessa ordem é a causa da maior parte das
 * rejeições — ver `REJEICOES` em `esocialTextos.ts`.
 */
export const EVENTOS: DefinicaoEvento[] = [
  { codigo: "S-1000", nome: "Informações do Empregador", categoria: "tabela", prazo: "Antes do primeiro evento", diaLimite: "evento" },
  { codigo: "S-1005", nome: "Tabela de Estabelecimentos", categoria: "tabela", prazo: "Antes de ser referenciada", diaLimite: "evento" },
  { codigo: "S-1010", nome: "Tabela de Rubricas", categoria: "tabela", prazo: "Antes de ser referenciada", diaLimite: "evento" },
  { codigo: "S-1020", nome: "Tabela de Lotações Tributárias", categoria: "tabela", prazo: "Antes de ser referenciada", diaLimite: "evento" },
  { codigo: "S-2190", nome: "Admissão Preliminar", categoria: "nao-periodico", prazo: "Até o fim do dia anterior ao início", diaLimite: "evento" },
  { codigo: "S-2200", nome: "Admissão de Trabalhador", categoria: "nao-periodico", prazo: "Até o dia anterior ao início das atividades", diaLimite: "evento" },
  { codigo: "S-2205", nome: "Alteração de Dados Cadastrais", categoria: "nao-periodico", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
  { codigo: "S-2206", nome: "Alteração de Contrato de Trabalho", categoria: "nao-periodico", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
  { codigo: "S-2230", nome: "Afastamento Temporário", categoria: "nao-periodico", prazo: "Conforme o tipo de afastamento", diaLimite: "evento" },
  { codigo: "S-2299", nome: "Desligamento", categoria: "nao-periodico", prazo: "Até 10 dias do desligamento", diaLimite: "evento" },
  { codigo: "S-1200", nome: "Remuneração do Trabalhador (RGPS)", categoria: "periodico", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
  { codigo: "S-1210", nome: "Pagamentos de Rendimentos do Trabalho", categoria: "periodico", prazo: "Até o dia 15 do mês seguinte ao pagamento", diaLimite: 15 },
  { codigo: "S-1280", nome: "Informações Complementares — CPRB", categoria: "periodico", prazo: "Antes do S-1299", diaLimite: 15 },
  { codigo: "S-1299", nome: "Fechamento dos Eventos Periódicos", categoria: "periodico", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
  { codigo: "R-2010", nome: "Retenção Previdenciária — Serviços Tomados", categoria: "reinf", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
  { codigo: "R-2099", nome: "Fechamento dos Eventos Periódicos — Reinf", categoria: "reinf", prazo: "Até o dia 15 do mês seguinte", diaLimite: 15 },
];

/** Índice por código, para a tela não varrer a lista a cada linha. */
export const EVENTO_POR_CODIGO: Record<string, DefinicaoEvento> = Object.fromEntries(
  EVENTOS.map((e) => [e.codigo, e]),
);

// ---------------------------------------------------------------------------
// Datas
// ---------------------------------------------------------------------------

/** Uma competência "AAAA-MM" partida em números. Devolve null se não casar. */
export function lerCompetencia(competencia: string): { ano: number; mes: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(competencia.trim());
  if (!m) return null;
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return null;
  return { ano, mes };
}

/** "2026-03" → "março de 2026". */
export function nomeDaCompetencia(competencia: string): string {
  const c = lerCompetencia(competencia);
  if (!c) return competencia;
  const meses = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];
  return `${meses[c.mes - 1]} de ${c.ano}`;
}

export type EhFeriado = (data: Date) => boolean;

/**
 * O dia útil em que um vencimento cai, recuando quando necessário.
 *
 * Recuar e não adiar: prazo fiscal que cai em dia não útil **antecipa**, e
 * adiar ensinaria o aluno a entregar em atraso. Vale para o dia 20 (DCTFWeb e
 * FGTS Digital) e para o dia 15 dos eventos periódicos.
 *
 * O laço tem trava de 15 voltas. Sem ela, um `ehFeriado` que devolvesse `true`
 * sempre — um bug de quem plugar o calendário — travaria a aba num laço infinito
 * em vez de mostrar uma data errada, e travar a tela é pior.
 */
export function diaUtilAnterior(data: Date, ehFeriado?: EhFeriado): Date {
  let d = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  for (let i = 0; i < 15; i++) {
    const fimDeSemana = d.getDay() === 0 || d.getDay() === 6;
    if (!fimDeSemana && !(ehFeriado?.(d) ?? false)) return d;
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  }
  return d;
}

/** dd/mm/aaaa. */
export const dataBr = (d: Date): string =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

/**
 * O vencimento de uma obrigação da competência: `dia` do MÊS SEGUINTE, recuado
 * para dia útil.
 *
 * O mês seguinte é obtido com `new Date(ano, mes, dia)` — `mes` já é 1-12 aqui,
 * e o construtor espera 0-11, então passar `mes` sem subtrair 1 é exatamente o
 * mês seguinte. Parece erro e não é; está escrito para ninguém "consertar".
 */
export function vencimentoDaCompetencia(
  competencia: string,
  dia: number,
  ehFeriado?: EhFeriado,
): Date | null {
  const c = lerCompetencia(competencia);
  if (!c) return null;
  return diaUtilAnterior(new Date(c.ano, c.mes, dia), ehFeriado);
}

// ---------------------------------------------------------------------------
// A competência inteira
// ---------------------------------------------------------------------------

export interface EntradaCompetencia {
  competencia: string;
  /** Um por empregado. O cronograma soma os holerites para achar a folha. */
  funcionarios: EntradaHolerite[];
  /** Os encargos, sem a folha — que é DERIVADA dos holerites. */
  encargos: Omit<EntradaEncargos, "folha">;
  admissoes: number;
  desligamentos: number;
  afastamentos: number;
  alteracoesCadastrais: number;
  alteracoesContratuais: number;
  /** Empresa que contrata serviços com retenção: entra na Reinf. */
  temReinf: boolean;
  ehFeriado?: EhFeriado;
}

export interface EventoDoCronograma {
  definicao: DefinicaoEvento;
  /** Quantos eventos deste tipo a competência tem. */
  quantidade: number;
  /** A data limite, quando o prazo é de calendário. */
  vencimento: Date | null;
  /** Por que este evento está (ou não) na lista. */
  motivo: string;
}

export interface ResultadoCompetencia {
  competencia: string;
  nome: string;
  holerites: ResultadoHolerite[];
  folhaBruta: number;
  totalInss: number;
  totalIrrf: number;
  totalFgts: number;
  totalLiquido: number;
  encargos: ResultadoEncargos;
  /** Custo total da competência: folha bruta + encargos patronais. */
  custoTotal: number;
  eventos: EventoDoCronograma[];
  vencimentoDctfweb: Date | null;
  vencimentoFgts: Date | null;
  vencimentoPeriodicos: Date | null;
  /** True enquanto o feriado não é considerado — a tela precisa dizer. */
  semFeriados: boolean;
}

/**
 * O cronograma de uma competência: os totais e os eventos a transmitir.
 *
 * **A folha é DERIVADA dos holerites, nunca digitada em separado.** Na origem
 * `EntradaEncargos.folha` era opcional e podia ser informada à mão, o que
 * permitia o estado em que os encargos são calculados sobre uma folha diferente
 * da que os holerites somam — dois números para a mesma coisa, discordando sem
 * ninguém ver. Aqui o tipo não deixa: `Omit<…, "folha">`.
 *
 * Um evento entra na lista com `quantidade > 0` quando a competência o exige, e
 * com `quantidade: 0` quando não — mas ele CONTINUA na lista, com o motivo. É a
 * diferença entre "não preciso enviar o S-2299 este mês" e "esqueci que existe o
 * S-2299", e a segunda é o que faz a empresa tomar multa.
 */
export function gerarCompetencia(e: EntradaCompetencia, p: ParametrosFolha): ResultadoCompetencia {
  const holerites = e.funcionarios.map((f) => calcularHolerite(f, p));

  const folhaBruta = centavos(holerites.reduce((s, h) => s + h.proventos.total, 0));
  const totalInss = centavos(holerites.reduce((s, h) => s + h.descontos.inss, 0));
  const totalIrrf = centavos(holerites.reduce((s, h) => s + h.descontos.irrf, 0));
  const totalFgts = centavos(holerites.reduce((s, h) => s + h.fgts, 0));
  const totalLiquido = centavos(holerites.reduce((s, h) => s + h.liquido, 0));

  const encargos = calcularEncargos({ ...e.encargos, folha: folhaBruta });

  const naCprb = e.encargos.regime === "cprb";
  const temFolha = holerites.length > 0;

  const quantidades: Record<string, { qtd: number; motivo: string }> = {
    // Tabelas: enviadas uma vez e mantidas. Ficam na lista como lembrete.
    "S-1000": { qtd: 0, motivo: "Enviado uma vez, na adesão. Só se houver alteração cadastral." },
    "S-1005": { qtd: 0, motivo: "Só quando um estabelecimento é criado ou alterado." },
    "S-1010": { qtd: 0, motivo: "Só quando uma rubrica é criada ou alterada." },
    "S-1020": { qtd: 0, motivo: "Só quando uma lotação tributária muda." },

    "S-2190": { qtd: 0, motivo: "Opcional: usado quando falta documento para o S-2200 completo." },
    "S-2200": {
      qtd: Math.max(0, e.admissoes),
      motivo: e.admissoes > 0
        ? `${e.admissoes} admissão(ões) no mês — cada uma até o dia ANTERIOR ao início`
        : "Nenhuma admissão informada.",
    },
    "S-2205": {
      qtd: Math.max(0, e.alteracoesCadastrais),
      motivo: e.alteracoesCadastrais > 0 ? "Correção de dados após o S-2200 processado." : "Nenhuma alteração cadastral.",
    },
    "S-2206": {
      qtd: Math.max(0, e.alteracoesContratuais),
      motivo: e.alteracoesContratuais > 0 ? "Cargo, salário, jornada ou lotação alterados." : "Nenhuma alteração contratual.",
    },
    "S-2230": {
      qtd: Math.max(0, e.afastamentos),
      motivo: e.afastamentos > 0 ? "Prazo varia com o tipo (acidente, doença, licença)." : "Nenhum afastamento.",
    },
    "S-2299": {
      qtd: Math.max(0, e.desligamentos),
      motivo: e.desligamentos > 0 ? `${e.desligamentos} desligamento(s) — até 10 dias de cada` : "Nenhum desligamento.",
    },

    "S-1200": {
      qtd: holerites.length,
      motivo: temFolha ? `${holerites.length} trabalhador(es) com remuneração no mês` : "Sem folha na competência.",
    },
    "S-1210": {
      qtd: temFolha ? 1 : 0,
      motivo: temFolha ? "Um por competência, informando os pagamentos efetuados." : "Sem pagamentos a informar.",
    },
    // O S-1280 só existe na CPRB — é ele que informa a desoneração.
    "S-1280": {
      qtd: naCprb ? 1 : 0,
      motivo: naCprb ? "Obrigatório na CPRB: informa a receita que substitui os 20%." : "Só no regime de CPRB.",
    },
    "S-1299": {
      qtd: temFolha ? 1 : 0,
      motivo: temFolha
        ? "O fechamento. Sem ele nada é apurado, e a DCTFWeb não é gerada."
        : "Sem movimento, o fechamento é 'sem movimento' e vai só em janeiro.",
    },

    "R-2010": {
      qtd: e.temReinf ? 1 : 0,
      motivo: e.temReinf ? "Serviços tomados com retenção de 11%." : "Só se a empresa tomar serviços com retenção.",
    },
    "R-2099": {
      qtd: e.temReinf ? 1 : 0,
      motivo: e.temReinf ? "Fechamento da Reinf, depois do R-2010." : "Só se houver movimento na Reinf.",
    },
  };

  const eventos: EventoDoCronograma[] = EVENTOS.map((definicao) => {
    const q = quantidades[definicao.codigo] ?? { qtd: 0, motivo: "" };
    return {
      definicao,
      quantidade: q.qtd,
      vencimento:
        typeof definicao.diaLimite === "number"
          ? vencimentoDaCompetencia(e.competencia, definicao.diaLimite, e.ehFeriado)
          : null,
      motivo: q.motivo,
    };
  });

  return {
    competencia: e.competencia,
    nome: nomeDaCompetencia(e.competencia),
    holerites,
    folhaBruta,
    totalInss,
    totalIrrf,
    totalFgts,
    totalLiquido,
    encargos,
    custoTotal: centavos(folhaBruta + encargos.total),
    eventos,
    // DCTFWeb e FGTS Digital vencem no dia 20; os eventos periódicos, no 15.
    vencimentoDctfweb: vencimentoDaCompetencia(e.competencia, 20, e.ehFeriado),
    vencimentoFgts: vencimentoDaCompetencia(e.competencia, 20, e.ehFeriado),
    vencimentoPeriodicos: vencimentoDaCompetencia(e.competencia, 15, e.ehFeriado),
    semFeriados: e.ehFeriado === undefined,
  };
}
